import { useRecordContext } from "react-admin";
import { runSafelyWithServerClient } from "../api/server.api";
import { assertNever } from "../backoffice.utils";
import type { Instructor } from "../providers/instructorsProvider";
import type { Report, ReportConfiguration, ReportFormat } from "../dialogs/CreateReportDialog";

export function useInstructorCalendarEventsReport(): Report {
  const instructor = useRecordContext<Instructor>();
  return {
    localizedTitle: "Stundenaufstellung",
    create: async (args: { config: ReportConfiguration; format: ReportFormat; onSuccess: () => void }) => {
      const {
        config: { from, to, includePlannedAppointments },
        format,
        onSuccess,
      } = args;
      await runSafelyWithServerClient(
        async (serverClient) => {
          const queries = { from: from.toString(), to: to.toString(), includePlannedAppointments };
          if (format === "PDF") {
            const pdfBlob = (await serverClient.getInstructorCalendarEventsReportPdf({
              params: { id: instructor.id },
              queries,
              responseType: "blob",
            })) as unknown as Blob;
            const pdfObjectUrl = URL.createObjectURL(pdfBlob);
            // Note: URL is intentionally not revoked - it must remain valid while the user views the PDF in the new tab.
            // The blob URL will be released when the user closes the preview tab.
            window.open(pdfObjectUrl, "_blank", "noopener,noreferrer");
          } else if (format === "Excel") {
            const xlsx = (await serverClient.getInstructorCalendarEventsReportExcel({
              params: { id: instructor.id },
              queries,
              responseType: "arraybuffer",
            })) as unknown as ArrayBuffer;
            const blob = new Blob([xlsx], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = `Stundenaufstellung ${instructor.firstName} ${instructor.lastName} (${from.toLocalizedString()} - ${to.toLocalizedString()}).xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(objectUrl);
          } else {
            assertNever(format);
          }
        },
        {
          errorMessage: `Fehler beim Erstellen der ${format} Stundenaufstellung.`,
          successMessage: "",
          onSuccess,
        },
      );
    },
  };
}
